import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function RelistModal({ open, onOpenChange, asset, onRelisted }) {
  const { walletAddress, connectWallet } = useAuth();
  const [listType, setListType] = useState(asset?.list_type || "fixed");
  const [priceEth, setPriceEth] = useState("");
  const [auctionDays, setAuctionDays] = useState(7);
  const [busy, setBusy] = useState(false);

  if (!asset) return null;

  const submit = async () => {
    if (!walletAddress) {
      try { await connectWallet(); } catch { return toast.error("Connect wallet"); }
    }
    setBusy(true);
    try {
      const payload = {
        list_type: listType,
        price_eth: listType === "fixed" ? Number(priceEth) : null,
        auction_end: listType === "auction" ? new Date(Date.now() + Number(auctionDays) * 86400000).toISOString() : null,
        wallet_signature: `sig_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      };
      await api.post(`/assets/${asset.id}/relist`, payload);
      toast.success(listType === "none" ? "Asset unlisted" : "Asset re-listed");
      onOpenChange(false);
      onRelisted && onRelisted();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Re-list asset</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-zinc-300">Listing type</Label>
            <RadioGroup value={listType} onValueChange={setListType} className="grid grid-cols-3 gap-3 mt-2">
              {["fixed", "auction", "none"].map((opt) => (
                <label key={opt} className={`p-2 rounded-md border text-center cursor-pointer text-sm capitalize ${listType === opt ? "border-cyan8r bg-cyan8r/5" : "border-white/10"}`}>
                  <RadioGroupItem value={opt} className="sr-only" />
                  {opt === "none" ? "Unlist" : opt}
                </label>
              ))}
            </RadioGroup>
          </div>
          {listType === "fixed" && (
            <div>
              <Label className="text-zinc-300">Price (ETH)</Label>
              <Input data-testid="relist-price-input" type="number" step="0.001" value={priceEth} onChange={(e) => setPriceEth(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" placeholder="0.05" />
            </div>
          )}
          {listType === "auction" && (
            <div>
              <Label className="text-zinc-300">Auction duration (days)</Label>
              <Input type="number" min={1} max={30} value={auctionDays} onChange={(e) => setAuctionDays(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" />
            </div>
          )}
          <button
            data-testid="relist-submit-btn"
            onClick={submit}
            disabled={busy}
            className="btn-web3 w-full py-3 rounded-md text-sm font-medium flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="animate-spin" size={16} />}
            {listType === "none" ? "Unlist" : "Sign & Re-list"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
